import {
  ACCOUNT_PRIVACY_VERSION,
  ACCOUNT_RISK_DISCLOSURE_VERSION,
  ACCOUNT_TERMS_VERSION
} from '@shouyi/shared';
import type { AgentReport, AnalysisJob, PublicDashboard, UserRole } from '@shouyi/shared';
import { defineStore } from 'pinia';

import { ApiError, api, post, userFacingError } from './api.js';

export type AnalysisReuse = {
  kind: 'new' | 'in_progress' | 'completed';
  expiresAt: string | null;
  quotaCharged: boolean;
  reportId: string | null;
};
export type AnalysisReply = { job: AnalysisJob; reuse: AnalysisReuse };
export type AnalysisPolicy = {
  reusePolicy: 'trading_day' | 'hours';
  reuseHours: number;
  description: string;
  analysisVersion: string;
};

export type CurrentUser = {
  id: string;
  role: UserRole;
  displayName: string;
  avatarUrl: string | null;
};

export type PaperAccount = {
  id: string;
  name: string;
  initialCash: number;
  availableCash: number;
  createdAt: string;
};

export type PaperOrder = {
  id: string;
  accountId: string;
  stockCode: string;
  side: 'buy' | 'sell';
  quantity: number;
  status: string;
  submittedAt: string;
  fillPrice: number | null;
  fillTradeDate: string | null;
  fees: number | null;
  rejectReason: string | null;
  quoteSource: string | null;
};

export type PaperPosition = {
  accountId: string;
  stockCode: string;
  quantity: number;
  averageCost: number;
  updatedAt: string;
};

export const usePlatformStore = defineStore('platform', {
  state: () => ({
    dashboard: null as PublicDashboard | null,
    user: null as CurrentUser | null,
    accountAuthReady: false,
    jobs: [] as AnalysisJob[],
    reports: [] as AgentReport[],
    analysisReuse: {} as Record<string, AnalysisReuse>,
    favorites: [] as string[],
    paperAccounts: [] as PaperAccount[],
    paperOrders: [] as PaperOrder[],
    paperPositions: [] as PaperPosition[],
    bootstrapped: false,
    loading: false,
    error: '',
    authError: '',
    workspaceError: '',
    paperError: '',
    workspaceLoading: false,
    paperLoading: false,
    sessionRevision: 0,
    bootstrapRevision: 0
  }),
  getters: {
    isAuthenticated: (state) => Boolean(state.user),
    isAdmin: (state) => state.user?.role === 'admin',
    candidates: (state) => state.dashboard?.snapshot?.candidates ?? []
  },
  actions: {
    clearPrivateData() {
      this.sessionRevision += 1;
      this.jobs = [];
      this.reports = [];
      this.analysisReuse = {};
      this.favorites = [];
      this.paperAccounts = [];
      this.paperOrders = [];
      this.paperPositions = [];
      this.workspaceError = '';
      this.paperError = '';
      this.workspaceLoading = false;
      this.paperLoading = false;
    },
    async bootstrap() {
      const revision = ++this.bootstrapRevision;
      const sessionRevision = this.sessionRevision;
      this.loading = true;
      this.error = '';
      this.authError = '';
      const [dashboard, auth] = await Promise.allSettled([
        api<PublicDashboard>('/api/v1/public/dashboard'),
        api<{ user: CurrentUser | null; accountAuthReady: boolean }>('/api/v1/auth/me')
      ]);
      if (revision !== this.bootstrapRevision) return;
      if (dashboard.status === 'fulfilled') this.dashboard = dashboard.value;
      else this.error = userFacingError(dashboard.reason);
      if (sessionRevision === this.sessionRevision) {
        if (auth.status === 'fulfilled') {
          if (this.user?.id !== auth.value.user?.id) this.clearPrivateData();
          this.user = auth.value.user;
          this.accountAuthReady = auth.value.accountAuthReady;
        } else {
          this.authError = userFacingError(auth.reason);
          this.clearPrivateData();
          this.user = null;
        }
      }
      this.loading = false;
      this.bootstrapped = true;
    },
    async login(username: string, password: string) {
      const result = await post<{ user: CurrentUser }>('/api/v1/auth/login', { username, password });
      this.clearPrivateData();
      this.authError = '';
      this.user = result.user;
    },
    async register(username: string, password: string, phone: string) {
      const result = await post<{ user: CurrentUser; phoneVerified: false }>('/api/v1/auth/register', {
        username,
        password,
        phone,
        acceptedLegalTerms: true,
        termsVersion: ACCOUNT_TERMS_VERSION,
        privacyVersion: ACCOUNT_PRIVACY_VERSION,
        riskDisclosureVersion: ACCOUNT_RISK_DISCLOSURE_VERSION
      });
      this.clearPrivateData();
      this.authError = '';
      this.user = result.user;
    },
    async logout() {
      try {
        await post('/api/v1/auth/logout', {});
      } finally {
        this.clearPrivateData();
        this.user = null;
      }
    },
    async loadWorkspace() {
      if (!this.user) return false;
      const revision = this.sessionRevision;
      const userId = this.user.id;
      this.workspaceLoading = true;
      this.workspaceError = '';
      try {
        const [tasks, reports, favorites] = await Promise.all([
          api<{ jobs: AnalysisJob[] }>('/api/v1/tasks'),
          api<{ reports: AgentReport[] }>('/api/v1/reports'),
          api<{ stockCodes: string[] }>('/api/v1/favorites')
        ]);
        if (revision !== this.sessionRevision || this.user?.id !== userId) return false;
        this.jobs = tasks.jobs;
        this.reports = reports.reports;
        this.favorites = favorites.stockCodes;
        return true;
      } catch (error) {
        if (revision !== this.sessionRevision) return false;
        if (error instanceof ApiError && error.status === 401) {
          this.clearPrivateData();
          this.user = null;
        }
        this.workspaceError = userFacingError(error);
        return false;
      } finally {
        if (revision === this.sessionRevision) this.workspaceLoading = false;
      }
    },
    async createAnalysis(stockCode: string, mode: 'live_research' | 'historical_strict' = 'live_research') {
      const revision = this.sessionRevision;
      const result = await post<{ job: AnalysisJob }>('/api/v1/analyses', { stockCode, mode });
      if (revision !== this.sessionRevision) return result.job;
      this.jobs = [result.job, ...this.jobs.filter((job) => job.id !== result.job.id)];
      return result.job;
    },
    async createAdHocAnalysis(stockCode: string): Promise<AnalysisReply> {
      const revision = this.sessionRevision;
      const result = await post<AnalysisReply>('/api/v1/analyses', { stockCode, mode: 'ad_hoc_research' });
      if (revision === this.sessionRevision) {
        this.jobs = [result.job, ...this.jobs.filter((job) => job.id !== result.job.id)];
        this.analysisReuse[result.job.id] = result.reuse;
      }
      return result;
    },
    async loadReport(reportId: string): Promise<AgentReport | null> {
      const revision = this.sessionRevision;
      const result = await api<{ report: AgentReport }>(`/api/v1/reports/${encodeURIComponent(reportId)}`);
      if (revision !== this.sessionRevision) return null;
      if (result.report.reportId !== reportId) throw new Error('report_identity_mismatch');
      this.reports = [result.report, ...this.reports.filter((report) => report.reportId !== reportId)];
      return result.report;
    },
    async loadJob(jobId: string): Promise<AnalysisJob | null> {
      const revision = this.sessionRevision;
      const result = await api<{ job: AnalysisJob; report: AgentReport | null }>(`/api/v1/tasks/${encodeURIComponent(jobId)}`);
      if (revision !== this.sessionRevision) return null;
      if (result.job.id !== jobId) throw new Error('job_identity_mismatch');
      this.jobs = [result.job, ...this.jobs.filter((job) => job.id !== jobId)];
      if (result.report && result.report.jobId === jobId) this.reports = [result.report, ...this.reports.filter((report) => report.reportId !== result.report!.reportId)];
      return result.job;
    },
    async createBatch(stockCodes: string[]) {
      const revision = this.sessionRevision;
      const result = await post<{ jobs: AnalysisJob[] }>('/api/v1/analyses/batch', { stockCodes, mode: 'live_research' });
      if (revision === this.sessionRevision) this.jobs = [...result.jobs, ...this.jobs.filter((job) => !result.jobs.some((row) => row.id === job.id))];
      return result.jobs;
    },
    async toggleFavorite(stockCode: string) {
      const revision = this.sessionRevision;
      const result = await post<{ active: boolean }>(`/api/v1/favorites/${stockCode}/toggle`, {});
      if (revision !== this.sessionRevision) return;
      this.favorites = result.active
        ? [...new Set([...this.favorites, stockCode])]
        : this.favorites.filter((code) => code !== stockCode);
    },
    async loadPaper() {
      if (!this.user) return false;
      const revision = this.sessionRevision;
      this.paperLoading = true;
      this.paperError = '';
      try {
        const [accounts, orders, positions] = await Promise.all([
          api<{ accounts: PaperAccount[] }>('/api/v1/paper/accounts'),
          api<{ orders: PaperOrder[] }>('/api/v1/paper/orders'),
          api<{ positions: PaperPosition[] }>('/api/v1/paper/positions')
        ]);
        if (revision !== this.sessionRevision) return false;
        this.paperAccounts = accounts.accounts;
        this.paperOrders = orders.orders;
        this.paperPositions = positions.positions;
        return true;
      } catch (error) {
        if (revision !== this.sessionRevision) return false;
        if (error instanceof ApiError && error.status === 401) {
          this.clearPrivateData();
          this.user = null;
        }
        this.paperError = userFacingError(error);
        return false;
      } finally {
        if (revision === this.sessionRevision) this.paperLoading = false;
      }
    },
    async createPaperAccount(name: string, initialCash: number) {
      const revision = this.sessionRevision;
      const result = await post<{ account: PaperAccount }>('/api/v1/paper/accounts', { name, initialCash });
      if (revision === this.sessionRevision) this.paperAccounts.push(result.account);
      return result.account;
    },
    async createPaperOrder(body: { accountId: string; stockCode: string; side: 'buy' | 'sell'; quantity: number; signalSnapshotId: string }) {
      const revision = this.sessionRevision;
      await post('/api/v1/paper/orders', { ...body, orderType: 'next_open' });
      if (revision === this.sessionRevision) await this.loadPaper();
    },
    async cancelPaperOrder(orderId: string) {
      const revision = this.sessionRevision;
      await post(`/api/v1/paper/orders/${orderId}/cancel`, {});
      if (revision === this.sessionRevision) await this.loadPaper();
    }
  }
});
